import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import { Field, Label, Input, FluentProvider, webLightTheme } from '@fluentui/react-components';
import React from 'react';
import { Controller, UseFormMethods } from 'react-hook-form';
import InlineFormInputErrorMessage from '../../common/errorContent/InlineFormInputErrorMessage';
import MonthDayPicker from '../../common/formFields/monthDayPicker/MonthDayPicker';
import { DoubleRequiredMarker, RequiredMarker } from '../../common/formFields/RequiredMarker';
import UtcDatePicker from '../../common/utcDatePicker/UtcDatePicker';
import RequiredFieldsNotification from '../RequiredNotification';
import LegalDocumentInfoAddress from './LegalDocumentInfoAddress';
import { ILegalDocumentInfo, RequiredFieldsType } from './types';

interface ILegalDocumentInfoFormProps {
  form: UseFormMethods<ILegalDocumentInfo>;
  data: ILegalDocumentInfo;
  isNonTransactional: boolean;
}

const LegalDocumentInfoForm = ({
  form,
  data,
  isNonTransactional,
}: ILegalDocumentInfoFormProps): JSX.Element => {
  const { control, errors, setValue } = form;

  const customLabelStyles = { paddingBottom: 0 };

  return (
    <FluentProvider theme={webLightTheme}>
      <Stack tokens={{ childrenGap: 5 }}>
        <RequiredFieldsNotification
          type={isNonTransactional ? RequiredFieldsType.StatSetup : RequiredFieldsType.SapSetup}
        />
        <Controller
          control={control}
          name="fullLegalName"
          defaultValue={data?.fullLegalName || ''}
          rules={{
            maxLength: {
              value: 200,
              message: 'Full Legal Name can only be a max of 200 characters',
            },
          }}
          render={({ onChange, onBlur, value, name, ref }) => (
            <Field
              label={
                <Label style={customLabelStyles}>
                  Full Legal Name{' '}
                  {isNonTransactional ? <DoubleRequiredMarker /> : <RequiredMarker />}
                </Label>
              }
              validationMessage={errors?.fullLegalName?.message}
            >
              <Input
                id="fullLegalName"
                value={value}
                name={name}
                ref={ref}
                onBlur={onBlur}
                onChange={(ev, inputData) => onChange(inputData.value)}
              />
            </Field>
          )}
        />
        <Controller
          control={control}
          name="sapLegalName"
          defaultValue={data?.sapLegalName || ''}
          rules={{
            maxLength: {
              value: 50,
              message: 'SAP Legal Name can only be a max of 50 characters',
            },
          }}
          render={({ onChange, onBlur, value, name, ref }) => (
            <Field
              label={
                <Label style={customLabelStyles}>
                  SAP Legal Name{' '}
                  {isNonTransactional ? <DoubleRequiredMarker /> : <RequiredMarker />}
                </Label>
              }
              validationMessage={errors?.sapLegalName?.message}
            >
              <Input
                id="sapLegalName"
                value={value}
                name={name}
                ref={ref}
                onBlur={onBlur}
                onChange={(ev, inputData) => onChange(inputData.value)}
              />
            </Field>
          )}
        />
        <Controller
          control={control}
          name="abbreviatedSapName"
          defaultValue={data?.abbreviatedSapName || ''}
          rules={{
            maxLength: {
              value: 25,
              message: 'Abbreviated SAP Name can only be a max of 25 characters',
            },
          }}
          render={({ onChange, onBlur, value, name, ref }) => (
            <Field
              label={
                <Label style={customLabelStyles}>
                  Abbreviated SAP Name{' '}
                  {isNonTransactional ? <DoubleRequiredMarker /> : <RequiredMarker />}
                </Label>
              }
              validationMessage={errors?.abbreviatedSapName?.message}
            >
              <Input
                id="abbreviatedSapName"
                value={value}
                name={name}
                ref={ref}
                onBlur={onBlur}
                onChange={(ev, inputData) => onChange(inputData.value)}
              />
            </Field>
          )}
        />
        <MonthDayPicker
          form={form}
          dueDateDay={data?.statutoryYearEndDay}
          dueDateMonth={data?.statutoryYearEndMonth}
          dayFormName="statutoryYearEndDay"
          monthFormName="statutoryYearEndMonth"
          label={
            <Label style={customLabelStyles}>
              Statutory Year End Month
              {isNonTransactional ? <DoubleRequiredMarker /> : <RequiredMarker />}
            </Label>
          }
          dayLabel={
            <Label style={customLabelStyles}>
              Day
              {isNonTransactional ? <DoubleRequiredMarker /> : <RequiredMarker />}
            </Label>
          }
          required={false}
        />
        <Controller
          name="incorporatedDate"
          control={control}
          defaultValue={data?.incorporatedDate}
          render={({ value }): React.ReactElement => (
            <div role="form">
              <UtcDatePicker
                label="Incorporated Date"
                allowTextInput
                disableAutoFocus={false}
                value={value}
                onSelectDate={(utcDateString: string): void => {
                  setValue('incorporatedDate', utcDateString);
                }}
              />
              <InlineFormInputErrorMessage errorMessage={errors?.incorporatedDate?.message} />
            </div>
          )}
        />
        <LegalDocumentInfoAddress
          form={form}
          legallyRegisteredAddress={data?.legallyRegisteredAddress}
          physicalAddress={data?.physicalAddress}
          isNonTransactional={isNonTransactional}
        />
      </Stack>
    </FluentProvider>
  );
};

export default LegalDocumentInfoForm;
